import type { CustomDisciplineGroupSnapshot } from "./custom-disciplines/group-snapshot.js";
import {
  athletePowerscoutBests,
  athletes,
  catAge,
  catPoid,
  disciplines,
  groups,
  meets,
  niveaux,
  records,
} from "@prisma-local/client";

import {
  AgeCategory,
  Discipline,
  License,
  LicenseTermsVersion,
  Prisma as PrismaRemote,
  Role,
  User,
  UserRole,
  WeightCategory,
} from "@prisma-remote/client";

/** Installer metadata subset returned by admin getUsers(). */
export interface UserInstallerInfo {
  fileName: string;
  fileSize: number;
  uploadedAt: Date | string;
  fileChecksum: string | null;
  uploadedByUserId: string | null;
}
// trim
export interface UserModel extends User {
  userRoles: (UserRole & { role: Role })[];
  /** Own license, or hydrated parent license for DisplayEditor on /users/me. */
  license?: License | null;
  // parentUserId comes from Prisma User (string | null) - do not redeclare as optional
  /** Licensed parent for DisplayEditor sub-accounts. */
  parentUser?: {
    id: string;
    login: string;
    license?: License | null;
  } | null;
  /** Speaker ↔ 0..N licenses (admin + /users/me). */
  speakerLicenses?: Array<{
    id?: string;
    licenseId?: string;
    license: Pick<License, "id" | "key" | "name"> & Partial<License>;
  }>;
  licenseTermsAcceptances?: Array<{
    id: string;
    licenseId: string;
    termsVersionId: string;
    termsVersion: string;
    acceptedAt: Date | string;
    acceptanceMethod: string;
  }>;
  licenseTermsAssignments?: Array<{
    id: string;
    userId: string;
    licenseId: string;
    termsVersionId: string;
    termsVersion?: LicenseTermsVersion;
  }>;
  installerPackage?: UserInstallerInfo | null;
}

export interface SynchronisedGroupInfos {
  id: string;
  name: string;
  athleteCount: number;
}

export interface SynchronisedMeetInfos {
  id: string;
  name: string;
  groupCount: number;
}

export interface ThemeColors {
  primary: string;
  primaryText: string;
  background: string;
  backgroundText: string;
  rankingText: string;
  ranking: string;
}

export interface TimerUpdatePayload {
  meetId: string;
  timeLeft: number;
  formatted: string;
}

export interface AttemptTimerPayload {
  id: number;
  remaining: number;
  formatted: string;
  /** Athlete whose next-attempt cell shows this ticket timer. */
  athleteId?: string;
  /** Lift field (attempt N+1) where the ticket chrono is displayed. */
  targetLiftField?: LiftField;
}

export interface AttemptTimersUpdatePayload {
  meetId: string;
  timers: AttemptTimerPayload[];
}

export const CONFIG_IDS = {
  primary: "Theme Primary Color",
  primaryText: "Theme Primary Text Color",
  background: "Theme Background Color",
  backgroundText: "Theme Background Text Color",
  rankingText: "Theme Ranking Text Color",
  ranking: "Theme Ranking Color",
} as const;

export const DEFAULTS: ThemeColors = {
  primary: "#0056B3", // MUI default primary
  primaryText: "#ffffff",
  background: "#ffffff",
  backgroundText: "#000000",
  rankingText: "#000000",
  ranking: "#ffffff",
};

export interface GroupWithAll extends PrismaRemote.GroupGetPayload<{}> {
  customIsOver?: boolean;
  customDisciplineGroup?: CustomDisciplineGroupSnapshot | null;
  athletes: AthleteWithAll[];
  selected: boolean;
  competitionFormat: string;
}

export interface RecordRemoteWithAll
  extends Omit<
    PrismaRemote.RecordGetPayload<{
      include: {
        ageCategory: true;
        weightCategory: true;
        discipline: true;
        organisation: true;
      };
    }>,
    | "organisationId"
    | "ageCategory"
    | "weightCategory"
    | "discipline"
    | "organisation"
  > {
  organisationId?: string;
  ageCategory: CompatibleRemoteAgeCategory;
  weightCategory: CompatibleRemoteWeightCategory;
  discipline: CompatibleRemoteDiscipline;
  organisation?: {
    id: string;
    name: string;
    code: string | null;
    countryCode: string | null;
    recordLabel: string | null;
    recordColor: string | null;
    recordBadgeUrl: string | null;
    parentId?: string | null;
  } | null;
}

export interface LevelRemoteWithAll
  extends Omit<
    PrismaRemote.LevelGetPayload<{
      include: { ageCategory: true; weightCategory: true; discipline: true };
    }>,
    "organisationId" | "ageCategory" | "weightCategory" | "discipline"
  > {
  organisationId?: string;
  ageCategory: CompatibleRemoteAgeCategory;
  weightCategory: CompatibleRemoteWeightCategory;
  discipline: CompatibleRemoteDiscipline;
}

export type PersonalBestField =
  | "best_squat_kg"
  | "best_bench_kg"
  | "best_deadlift_kg"
  | "best_total_kg";

export type AutoField =
  | "squat1Auto"
  | "squat2Auto"
  | "squat3Auto"
  | "bench1Auto"
  | "bench2Auto"
  | "bench3Auto"
  | "deadlift1Auto"
  | "deadlift2Auto"
  | "deadlift3Auto";

export type OverturnField =
  | "squat1Overturn"
  | "squat2Overturn"
  | "squat3Overturn"
  | "bench1Overturn"
  | "bench2Overturn"
  | "bench3Overturn"
  | "deadlift1Overturn"
  | "deadlift2Overturn"
  | "deadlift3Overturn";

export type OverturnColorField =
  | "squat1OverturnColor"
  | "squat2OverturnColor"
  | "squat3OverturnColor"
  | "bench1OverturnColor"
  | "bench2OverturnColor"
  | "bench3OverturnColor"
  | "deadlift1OverturnColor"
  | "deadlift2OverturnColor"
  | "deadlift3OverturnColor";

export type RefereeColorField =
  | "squat1LeftColor"
  | "squat1ChiefColor"
  | "squat1RightColor"
  | "squat2LeftColor"
  | "squat2ChiefColor"
  | "squat2RightColor"
  | "squat3LeftColor"
  | "squat3ChiefColor"
  | "squat3RightColor"
  | "bench1LeftColor"
  | "bench1ChiefColor"
  | "bench1RightColor"
  | "bench2LeftColor"
  | "bench2ChiefColor"
  | "bench2RightColor"
  | "bench3LeftColor"
  | "bench3ChiefColor"
  | "bench3RightColor"
  | "deadlift1LeftColor"
  | "deadlift1ChiefColor"
  | "deadlift1RightColor"
  | "deadlift2LeftColor"
  | "deadlift2ChiefColor"
  | "deadlift2RightColor"
  | "deadlift3LeftColor"
  | "deadlift3ChiefColor"
  | "deadlift3RightColor";

/** Remote discipline with federation fields optional for legacy snapshots. */
export type CompatibleRemoteDiscipline = Omit<
  Discipline,
  "organisationId" | "displayOrder"
> &
  Partial<Pick<Discipline, "organisationId" | "displayOrder">>;
/** Remote age category with federation fields optional for legacy snapshots. */
export type CompatibleRemoteAgeCategory = Omit<
  AgeCategory,
  | "organisationId"
  | "displayOrder"
  | "isOpen"
  | "sex"
  | "isSenior"
  | "isHandisport"
  | "priority"
> &
  Partial<
    Pick<
      AgeCategory,
      | "organisationId"
      | "displayOrder"
      | "isOpen"
      | "sex"
      | "isSenior"
      | "isHandisport"
      | "priority"
    >
  >;
/** Remote weight category with federation fields optional for legacy snapshots. */
export type CompatibleRemoteWeightCategory = Omit<
  WeightCategory,
  | "organisationId"
  | "displayOrder"
  | "disciplineId"
  | "ageCategoryId"
  | "isHandisport"
  | "isOpen"
  | "minInclusive"
  | "maxInclusive"
> &
  Partial<
    Pick<
      WeightCategory,
      | "organisationId"
      | "displayOrder"
      | "disciplineId"
      | "ageCategoryId"
      | "isHandisport"
      | "isOpen"
      | "minInclusive"
      | "maxInclusive"
    >
  >;
export interface AthleteWithAll
  extends Omit<
    PrismaRemote.AthleteGetPayload<{
      include: {
        ageCategory: true;
        discipline: true;
        weightCategory: true;
      };
    }>,
    "powerscoutLifterId" | "ageCategory" | "discipline" | "weightCategory"
  > {
  ageCategory: CompatibleRemoteAgeCategory;
  discipline: CompatibleRemoteDiscipline;
  weightCategory: CompatibleRemoteWeightCategory;
  powerscoutBests?: athletePowerscoutBests | null;
  /** Durable Powerscout lifter link (remote MySQL); always number | null (never undefined). */
  powerscoutLifterId: number | null;
}

export interface MeetWithLigue
  extends Omit<
    PrismaRemote.MeetGetPayload<{
      include: {
        ligue: true;
      };
    }>,
    "organisationId"
  > {
  organisationId?: string;
  licenseKey: string;
}

export interface MeetWithAll
  extends Omit<
    PrismaRemote.MeetGetPayload<{
      include: {
        ligue: true;
      };
    }>,
    "organisationId" | "pointsFormula" | "isNational"
  > {
  organisationId?: string;
  /** Scoring formula: "GL" (default) or "DOTS"; optional on legacy payloads. */
  pointsFormula?: string;
  /** Legacy payloads default to rankings by age category. */
  isNational?: boolean;
  records?: records[];
  niveaux?: niveaux[];
  groups: GroupWithAll[];
  /** Per-plateau stream overrides (loaded via include or live payload). */
  plateauStreams?: Array<{
    id?: string;
    plateauNumber: number;
    url: string;
  }>;
  machineId?: string;
  localWidgetsURLs?: string[];
  /** Runtime selection owned by the competition post, never inferred by a viewer. */
  currentCustomAthleteId?: string | null;
}

/** Federation-aware local rows after the mandatory local migration. */
export type CompatibleLocalDiscipline = Omit<
  disciplines,
  "organisationId" | "displayOrder"
> &
  Partial<Pick<disciplines, "organisationId" | "displayOrder">>;
export type CompatibleLocalAgeCategory = Omit<
  catAge,
  | "organisationId"
  | "isOpen"
  | "displayOrder"
  | "sex"
  | "isSenior"
  | "isHandisport"
  | "priority"
> &
  Partial<
    Pick<
      catAge,
      | "organisationId"
      | "isOpen"
      | "displayOrder"
      | "sex"
      | "isSenior"
      | "isHandisport"
      | "priority"
    >
  >;
export type CompatibleLocalWeightCategory = Omit<
  catPoid,
  | "organisationId"
  | "displayOrder"
  | "disciplineId"
  | "ageCategoryId"
  | "isHandisport"
  | "isOpen"
  | "minInclusive"
  | "maxInclusive"
> &
  Partial<
    Pick<
      catPoid,
      | "organisationId"
      | "displayOrder"
      | "disciplineId"
      | "ageCategoryId"
      | "isHandisport"
      | "isOpen"
      | "minInclusive"
      | "maxInclusive"
    >
  >;
export interface AthletesModel extends athletes {
  group?: groups;
  discipline?: CompatibleLocalDiscipline;
  catPoid?: CompatibleLocalWeightCategory;
  catAge?: CompatibleLocalAgeCategory;
  niveau?: niveaux;
  powerscoutBests?: athletePowerscoutBests | null;

  originalIndex?: number;
}

/**
 * Level names in order
 */
export const NIVEAUX_NAMES = ["R3", "R2", "R1", "N2", "N1", "Europe", "Monde"];

export interface GroupModel extends groups {
  customIsOver?: boolean;
  customDisciplineGroup?: CustomDisciplineGroupSnapshot | null;
  athletes?: AthletesModel[];
  sortType?: "default" | "barres" | "category";
}

export interface MeetModel
  extends Omit<meets, "organisationId" | "pointsFormula" | "isNational"> {
  organisationId?: string;
  /** Scoring formula: "GL" (default) or "DOTS"; optional on legacy payloads. */
  pointsFormula?: string;
  /** Legacy payloads omit this flag and keep age-category rankings. */
  isNational?: boolean;
  /**
   * Legacy league relation. Organisation is the source of truth; ligue may be
   * absent on partial payloads (e.g. get-meets select without id) or when the
   * UI only carries organisationId.
   */
  ligue?: {
    id?: number;
    nom?: string;
  } | null;
  groups: GroupModel[];
  records?: records[];
  niveaux?: niveaux[];
  /** Per-plateau stream overrides from meetPlateauStreams. */
  plateauStreams?: Array<{
    id?: string;
    plateauNumber: number;
    url: string;
  }>;
  licenseKey?: string; // Clé de licence utilisée pour créer ce meet
  /** Marks the automatic safety copy created before restoring a backup. */
  backupReason?: "BEFORE_RESTORE";
  machineId?: string;
  localWidgetsURLs?: string[];
  currentCustomAthleteId?: string | null;
}

export interface RecordModel
  extends Omit<
    records,
    | "organisationId"
    | "sheetDate"
    | "organisationName"
    | "recordLabel"
    | "recordColor"
    | "recordBadgeUrl"
  > {
  organisationId?: string;
  sheetDate?: string;
  organisationName?: string | null;
  recordLabel?: string | null;
  recordColor?: string | null;
  recordBadgeUrl?: string | null;
  catAge?: CompatibleLocalAgeCategory;
  catPoid?: CompatibleLocalWeightCategory;
  discipline?: CompatibleLocalDiscipline;
}

export interface NiveauxModel
  extends Omit<niveaux, "organisationId" | "sheetDate"> {
  organisationId?: string;
  sheetDate?: string;
  discipline?: CompatibleLocalDiscipline;
  catAge?: CompatibleLocalAgeCategory;
  catPoid?: CompatibleLocalWeightCategory;
}

export interface CatAgeModel extends CompatibleLocalAgeCategory {
  discipline?: CompatibleLocalDiscipline;
}

export type MenuItemType = {
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
};

export type MenuGroupType = {
  title: any;
  icon: React.ReactNode;
  items: MenuItemType[];
};

export enum LiftStatus {
  ANNONCED = "ANNONCED",
  LIFT = "LIFT",
  NO_LIFT = "NO_LIFT",
}

export type ClientType = "APP" | "WEBAPP";

export interface Exercise {
  name: "squat" | "bench" | "deadlift";
  lifts: LiftField[];
}
export const Exercises: Exercise[] = [
  { name: "squat", lifts: ["squat1", "squat2", "squat3"] },
  { name: "bench", lifts: ["bench1", "bench2", "bench3"] },
  { name: "deadlift", lifts: ["deadlift1", "deadlift2", "deadlift3"] },
];

export const exerciseList = {
  squat: "squat",
  bench: "bench",
  deadlift: "deadlift",
};
export type LiftField =
  | "squat1"
  | "squat2"
  | "squat3"
  | "bench1"
  | "bench2"
  | "bench3"
  | "deadlift1"
  | "deadlift2"
  | "deadlift3";

export const liftFields: LiftField[] = [
  "squat1",
  "squat2",
  "squat3",
  "bench1",
  "bench2",
  "bench3",
  "deadlift1",
  "deadlift2",
  "deadlift3",
];

export enum PositionRack {
  EXT = "EXT",
  INT = "INT",
  DemiDroit = "1/2 D",
  DemiGauche = "1/2 G",
}

export const positionsRack = [
  PositionRack.EXT,
  PositionRack.INT,
  PositionRack.DemiDroit,
  PositionRack.DemiGauche,
];

export type DisplayMode =
  | "athlete_display"
  | "loader_display"
  | "ranking_display"
  | "public_display"
  | "main"
  | "referee_lights";

export const windowModes: Record<DisplayMode, DisplayMode> = {
  athlete_display: "athlete_display",
  loader_display: "loader_display",
  ranking_display: "ranking_display",
  public_display: "public_display",
  main: "main",
  referee_lights: "referee_lights",
};

export interface NextLiftData {
  athlete: AthletesModel | undefined;
  lift: LiftField | null;
}

// Interfaces pour l'affichage public
export interface PublicDisplayData {
  currentAthlete: AthletesModel | null;
  categoryAthletes: AthletesModel[];
  currentLift: LiftField | null;
  competitionFormat: CompetitionFormat;
  categoryInfo: {
    ageCategory: string;
    weightCategory: string;
    discipline: string;
    sexe: string;
  };
}

export interface AttemptStatus {
  weight: number;
  status: "success" | "failed" | "announced" | "current";
  attemptNumber: number;
  movement: string;
}

export interface PublicDisplayColors extends ThemeColors {
  success: string;
  failed: string;
  announced: string;
  current: string;
}

export interface MeetPatch {
  id: string;
  data: PrismaRemote.MeetUpdateInput;
}

export interface GroupPatch {
  id: string;
  meetId: string;
  data: PrismaRemote.GroupUpdateInput;
}

export interface AthletePatch {
  id: string;
  meetId: string;
  groupId: string;
  data: PrismaRemote.AthleteUpdateInput;
}

export interface CurrentAthleteWidgetData {
  athlete: AthletesModel | undefined;
  lift: LiftField;
  exercise: Exercise | null;
  attempt: number;
  placementIfSuccessful: number | null;
  meetName: string;
  startDate: Date;
}

export type SyncResult =
  | "SNAPSHOT"
  | "REVISION"
  /** Same revision as the last applied event (e.g. athlete-patch + current-athlete). */
  | "COALESCED"
  | "DUPLICATE"
  | "RESYNC";
export type MeetPayload = { id: string; auth_token?: string };

export interface CurrentCategoryWidgetData {
  athlete: AthletesModel;
  sorted: AthletesModel[];
  placementIfSuccessful: number | null;
}

export type RefereeType =
  | "left"
  | "chief"
  | "right"
  | "jury_1"
  | "jury_2"
  | "jury_3";

// Un seul objet de résultat pour un juge ou un membre du jury
export interface RefereeInfo {
  referee_type: RefereeType;
  // pour left, chief, right
  passed?: 0 | 1;
  // couleur du vote ou du cercle
  color?: string;
  // pour jury_1, jury_2, jury_3
  juryEnabled?: 0 | 1;
}

// Le payload complet reçu par socket.on('currentRefereeInfo', …)
export interface CurrentRefereeInfo {
  result: RefereeInfo[];
  profileId?: string;
  /** Custom scoring output; its interpretation belongs to discipline Behavior. */
  refereeing?: {
    votes: Record<string, unknown>;
    state: Record<string, unknown>;
    result: unknown;
    revision: number;
  };
}

export interface DisplaySettings {
  powerlifting: 0 | 1;
  parapowerlifting: 0 | 1;
  weightlifting: 0 | 1;
}

// Le type complet émis par l’événement "settings"
export interface SettingsPayload {
  settings: DisplaySettings[];
}

export interface LicenceValidationResponse {
  valid: boolean;
  reason?: string;

  license?: {
    key?: string;
    name?: string;
    expirationDate?: Date;
    maxAthletes?: number;
    currentAthleteCount?: number;
    hasBackupAccess?: boolean;
    hasLiveAccess?: boolean;
    hasPlanningAccess?: boolean;
    hasRemoteCustomDisplayAccess?: boolean;
    hasLayoutEditorAccess?: boolean;
    hasCustomDisplayAccess?: boolean;
    hasControlRoomAccess?: boolean;
    maxCustomScreens?: number;
    maxCustomWidgets?: number;
  };
}

export interface LicenseActivationResponse {
  success: boolean;
  message?: string;

  license?: {
    key?: string;
    name?: string;
    expirationDate?: Date;
    maxAthletes?: number;
    currentAthleteCount?: number;
    hasBackupAccess?: boolean;
    hasLiveAccess?: boolean;
    hasPlanningAccess?: boolean;
    hasRemoteCustomDisplayAccess?: boolean;
    hasLayoutEditorAccess?: boolean;
    hasCustomDisplayAccess?: boolean;
    hasControlRoomAccess?: boolean;
    maxCustomScreens?: number;
    maxCustomWidgets?: number;
  };
}

export interface ElectronServiceConfig {
  running: boolean;
  port: number;
  url: string;
}

// Type global
export interface ElectronAppConfig {
  web: ElectronServiceConfig;
  socket: ElectronServiceConfig;
}

export interface AthleteCountData {
  total: number; // Nombre total d'athlètes actuels
  completed: number; // Participations terminées actuellement présentes
  cumulative: number; // Participations terminées comptabilisées dans l'historique
  /** Stable participation identities already charged, including deleted athletes. */
  countedAthleteIds?: string[];
  /** Current reservations not already included in cumulative. */
  nonFinished?: number;
  timestamp: number;
  hash: string;
}

export interface AthleteCountValidationResult {
  isValid: boolean;
  reason?:
    | "LIMIT_EXCEEDED"
    | "CORRUPTED_DATA"
    | "VALIDATION_ERROR"
    | "SYNC_FAILED"
    | "SERVER_REJECTED";
  currentCount: number;
  maxAthletes: number;
  shouldTryOnline: boolean;
  lastValidated: number;
  // Nouveaux champs pour le blocage strict
  requiresBlock?: boolean;
  blockReason?: string;
}

// ===== SYSTÈME DE FORMATS DE COMPÉTITION =====

/**
 * Enum définissant les formats de compétition supportés
 */
export enum CompetitionFormat {
  FULL_POWER = "FULL_POWER", // FA + PL (3 mouvements: squat, bench, deadlift)
  SINGLE_LIFT = "SINGLE_LIFT", // DC + BP (1 mouvement: bench uniquement)
}

export enum DisciplineId {
  FA = 1,
  PL = 3,
  DC = 6,
  BP = 7,
}

/** MM disciplines mapped to Powerscout event+equipment (auto-sync + Electron column). */
export const POWERSCOUT_SUPPORTED_DISCIPLINE_IDS: readonly DisciplineId[] = [
  DisciplineId.FA,
  DisciplineId.PL,
  DisciplineId.DC,
  DisciplineId.BP,
];

/**
 * Returns whether Powerscout auto-sync applies to this MM discipline.
 *
 * @param disciplineId - Local/remote MM discipline id (unknown ids stay unsupported).
 * @returns True for FA, PL, DC and BP.
 */
export function isPowerscoutSupportedDiscipline(
  disciplineId: number | null | undefined,
): boolean {
  if (disciplineId == null) {
    return false;
  }
  return (POWERSCOUT_SUPPORTED_DISCIPLINE_IDS as readonly number[]).includes(
    disciplineId,
  );
}

/**
 * Mapping des codes de discipline vers leur format de compétition
 */
export const disciplineFormatMap: Record<string, CompetitionFormat> = {
  FA: CompetitionFormat.FULL_POWER,
  PL: CompetitionFormat.FULL_POWER,
  DC: CompetitionFormat.SINGLE_LIFT,
  BP: CompetitionFormat.SINGLE_LIFT,
};

/**
 * Configuration des exercices actifs par format de compétition
 */
export const exercisesByFormat: Record<CompetitionFormat, Exercise[]> = {
  [CompetitionFormat.FULL_POWER]: [
    { name: "squat", lifts: ["squat1", "squat2", "squat3"] },
    { name: "bench", lifts: ["bench1", "bench2", "bench3"] },
    { name: "deadlift", lifts: ["deadlift1", "deadlift2", "deadlift3"] },
  ],
  [CompetitionFormat.SINGLE_LIFT]: [
    { name: "bench", lifts: ["bench1", "bench2", "bench3"] },
  ],
};

// ===== SYSTÈME DE COMPTAGE DES CHANGEMENTS DE BARRES =====

/**
 * Type for lift change counter fields (only tracked lifts)
 */
export type ChangeCountField =
  | "squat1Changes"
  | "bench1Changes"
  | "bench3Changes"
  | "deadlift1Changes"
  | "deadlift3Changes";

/**
 * Gets the change counter field name for a given lift field
 * Returns null if this lift doesn't track changes
 *
 * @param liftField - The lift field to get the counter for
 * @returns The change counter field name or null if not tracked
 *
 * @example
 * getChangeCountField('squat1') // returns 'squat1Changes'
 * getChangeCountField('squat2') // returns null (not tracked)
 */
export const getChangeCountField = (
  liftField: LiftField,
): ChangeCountField | null => {
  const trackedFields: Partial<Record<LiftField, ChangeCountField>> = {
    squat1: "squat1Changes",
    bench1: "bench1Changes",
    bench3: "bench3Changes",
    deadlift1: "deadlift1Changes",
    deadlift3: "deadlift3Changes",
  };

  return trackedFields[liftField] || null;
};

/**
 * Gets the maximum allowed changes for a lift based on competition format
 * Returns null if this lift doesn't track changes
 *
 * @param liftField - The lift field to check
 * @param competitionFormat - The competition format (FULL_POWER or SINGLE_LIFT)
 * @returns The maximum number of changes allowed or null if not tracked
 *
 * @example
 * getMaxChangesAllowed('squat1', CompetitionFormat.FULL_POWER) // returns 1 (opener)
 * getMaxChangesAllowed('deadlift3', CompetitionFormat.FULL_POWER) // returns 2 (last lift in full power)
 * getMaxChangesAllowed('bench3', CompetitionFormat.SINGLE_LIFT) // returns 2 (last lift in single lift)
 * getMaxChangesAllowed('squat2', CompetitionFormat.FULL_POWER) // returns null (not tracked)
 */
export const getMaxChangesAllowed = (
  liftField: LiftField,
  competitionFormat: CompetitionFormat,
): number | null => {
  // Openers (1st lifts) : 1 changement max
  if (
    liftField === "squat1" ||
    liftField === "bench1" ||
    liftField === "deadlift1"
  ) {
    return 1;
  }

  // Last deadlift in FULL_POWER : 2 changements max
  if (
    liftField === "deadlift3" &&
    competitionFormat === CompetitionFormat.FULL_POWER
  ) {
    return 2;
  }

  // Last bench in SINGLE_LIFT : 2 changements max
  if (
    liftField === "bench3" &&
    competitionFormat === CompetitionFormat.SINGLE_LIFT
  ) {
    return 2;
  }

  // Other lifts : no tracking
  return null;
};

/**
 * Checks if a lift field should track changes
 *
 * @param liftField - The lift field to check
 * @param competitionFormat - The competition format
 * @returns True if the lift should track changes, false otherwise
 *
 * @example
 * shouldTrackChanges('squat1', CompetitionFormat.FULL_POWER) // returns true
 * shouldTrackChanges('squat2', CompetitionFormat.FULL_POWER) // returns false
 */
export const shouldTrackChanges = (
  liftField: LiftField,
  competitionFormat: CompetitionFormat,
): boolean => {
  return getMaxChangesAllowed(liftField, competitionFormat) !== null;
};

export type SyncMeta = {
  meetId: string;
  rev: number;
  sessionId: string;
};

export type LevelRemote = {
  id: number;
  name: string;
  minTotal: number;
  maxTotal: number;
  weightCategoryId: number;
  ageCategoryId: number;
  disciplineId: number;
  sheetDate: string;
};
